import { SagaIterator } from 'redux-saga';
import i18next from 'i18next';
import {
  PapiDeviation,
  PapiRequestStudioOrdersInit,
  PapiStudioOrdersInit,
} from '@wix/da-papi-types';
import {
  changeOriginalStatus,
  changeOriginalStatusSuccess,
  declineOriginalOrder,
  declineOriginalOrderSuccess,
  fetchOriginalsOrderDetails,
  acceptOriginalOrder,
  acceptOriginalOrderSuccess,
  OriginalsOrderFlowContext,
  toggleOriginalStatus,
  fetchNextOrders,
  refreshOrdersStreams,
} from '../../actions/studio/originals';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { call, all, put, select, takeLatest } from 'redux-saga/effects';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { takeStreamFetch, fetchStreamContents } from '@wix/da-shared-react/pkg/Stream/helpers';
import { getNextOffset, getPrevOffset } from '@wix/da-shared-react/pkg/Stream/withOffset/selectors';
import { getStreamParams, getTotal } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { OriginalStatusType } from '../../types/originals';
import { getIsOriginalOpen } from '../../selectors/studio/originals';
import {
  STUDIO_ORDERS_IN_PROGRESS_STREAM,
  STUDIO_ORDERS_PENDING_STREAM,
  STUDIO_ORDERS_COMPLETED_STREAM,
  STUDIO_ORDERS_REJECTED_STREAM,
  STUDIO_ORDERS_BY_DEVIATION_STREAM,
} from '../../../constants';
import { getActiveSection } from '../../selectors/studio/page';
import { StudioSection } from '../../types/store';

export function* changeOriginalStatusSaga(
  action: ReturnType<typeof changeOriginalStatus>
): SagaIterator {
  const { deviation, status } = action.payload;
  const isOpen = status === OriginalStatusType.OPEN;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/originals/change_status',
      data: {
        deviationid: deviation.deviationId,
        is_open: isOpen,
      },
    },
    undefined,
    'dashared'
  );

  if (!response || isApiError(response) || !response.success) {
    yield put(addErrorNotification(i18next.t('common.somethingWentWrong')));
    return;
  }

  const updatedDeviation = { ...deviation };
  if (!updatedDeviation.originals) {
    updatedDeviation.originals = {} as any;
  }
  (updatedDeviation.originals as any).isOpen = isOpen;
  const { entities } = normalize(updatedDeviation, deviationSchema);
  yield put(putEntities({ entities }));
  yield put(changeOriginalStatusSuccess(updatedDeviation));
}

export function* toggleOriginalStatusSaga(
  action: ReturnType<typeof toggleOriginalStatus>
): SagaIterator {
  const deviation = action.payload as PapiDeviation;
  const isOpen = yield select(getIsOriginalOpen, deviation.deviationId);
  const status = isOpen ? OriginalStatusType.CLOSED : OriginalStatusType.OPEN;
  yield put(changeOriginalStatus({ deviation, status }));
}

export function* fetchOriginalsOrderDetailsSaga(
  action: ReturnType<typeof fetchOriginalsOrderDetails>
): SagaIterator {
  const { deviationId, responseId, onSuccess, onError } = action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: `/originals/order`,
      params: {
        originals_deviationid: deviationId,
        responseid: responseId,
      },
    },
    undefined,
    'dashared'
  );
  if (response && !isApiError(response)) {
    yield call(onSuccess, response);
  } else {
    yield call(onError, response);
  }
}

export function* declineOriginalOrderSaga(
  action: ReturnType<typeof declineOriginalOrder>
): SagaIterator {
  const { deviationId, responseId, reason, onSuccess, onError, parentDeviation } = action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/originals/order/reject`,
      data: {
        originals_deviationid: deviationId,
        responseid: responseId,
        reason,
      },
    },
    undefined,
    'dashared'
  );

  if (response && !isApiError(response)) {
    yield put(declineOriginalOrderSuccess({ parentDeviation }));
    yield call(onSuccess);
  } else {
    yield call(onError, response);
  }
}

export function* acceptOriginalOrderSaga(
  action: ReturnType<typeof acceptOriginalOrder>
): SagaIterator {
  const { deviationId, responseId, onSuccess, onError, parentDeviation } = action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/originals/order/accept`,
      data: {
        originals_deviationid: deviationId,
        responseid: responseId,
      },
    },
    undefined,
    'dashared'
  );
  if (response && !isApiError(response)) {
    yield put(acceptOriginalOrderSuccess({ parentDeviation }));
    yield call(onSuccess);
  } else {
    yield call(onError, response);
  }
}

export function* fetchOrdersStream({
  streamId,
  offset,
  limit,
  reset,
}: {
  streamId: string;
  offset: number;
  limit?: number;
  reset?: boolean;
}) {
  const { params } = yield select(state => ({
    params: getStreamParams(state, streamId),
  }));

  const { view, status, deviationid } = params;

  yield fetchStreamContents<PapiRequestStudioOrdersInit, PapiStudioOrdersInit>({
    streamId: streamId,
    params: {
      init: false,
      view,
      status,
      deviationid,
      offset,
      limit_per_grouping: limit,
    },
    url: 'studio/orders/init',
    papiProject: 'v1',
    getStreamData: response => {
      let items;
      let hasMore;
      let nextOffset;
      if (status) {
        const orderGroup = response.orders.find(order => order.status === status);
        items = orderGroup?.results || [];
        hasMore = orderGroup?.hasMore ?? false;
        nextOffset = orderGroup?.nextOffset ?? 0;
      } else if (deviationid) {
        const orderGroup = response.orders.find(
          order => order.deviation?.deviationId === deviationid
        );
        items = orderGroup?.results || [];
        hasMore = orderGroup?.hasMore ?? false;
        nextOffset = orderGroup?.nextOffset ?? 0;
      } else {
        items = response.orders;
        hasMore = response.hasMore ?? false;
        nextOffset = response.nextOffset ?? 0;
      }
      return {
        hasMore,
        items,
        nextOffset,
        reset,
      };
    },
  });
}

export function* fetchNextOrdersSaga({
  payload: { streamId },
}: ReturnType<typeof fetchNextOrders>) {
  const { offset } = yield select(state => ({
    offset: getNextOffset(state, streamId),
  }));

  yield call(fetchOrdersStream, { streamId, offset });
}

export function* refreshOrdersStreamsSaga(action: ReturnType<typeof refreshOrdersStreams>) {
  const deviationId = action.payload?.deviationId;
  const activeSection = yield select(getActiveSection);
  let streamIds: string[] = [];
  if (activeSection === StudioSection.ORDERS_STATUS) {
    streamIds = [
      STUDIO_ORDERS_IN_PROGRESS_STREAM.streamId,
      STUDIO_ORDERS_PENDING_STREAM.streamId,
      STUDIO_ORDERS_COMPLETED_STREAM.streamId,
      STUDIO_ORDERS_REJECTED_STREAM.streamId,
    ];
  } else if (activeSection === StudioSection.ORDERS_ORIGINALS) {
    if (deviationId != null) {
      streamIds = [STUDIO_ORDERS_BY_DEVIATION_STREAM.getStreamIdForDeviation(deviationId)];
    } else {
      streamIds = [STUDIO_ORDERS_BY_DEVIATION_STREAM.streamId];
    }
  }
  for (const streamId of streamIds) {
    const offset = yield select(getPrevOffset, streamId);
    const limit = yield select(getTotal, streamId);
    yield call(fetchOrdersStream, { streamId, offset, limit, reset: true });
  }
}

/** Listens for accept/decline API success — refresh the relevant orders streams. */
export function* originalsOrderFlowSuccessRefreshSaga(action: {
  payload: OriginalsOrderFlowContext;
}): SagaIterator {
  const { parentDeviation } = action.payload ?? {};
  const deviationId = parentDeviation?.deviationId;
  yield put(refreshOrdersStreams({ deviationId }));
}

export default function* originalsSaga(): any {
  yield all([
    takeLatest(changeOriginalStatus, changeOriginalStatusSaga),
    takeLatest(toggleOriginalStatus, toggleOriginalStatusSaga),
    takeLatest(fetchOriginalsOrderDetails, fetchOriginalsOrderDetailsSaga),
    takeLatest(declineOriginalOrder, declineOriginalOrderSaga),
    takeLatest(acceptOriginalOrder, acceptOriginalOrderSaga),
    takeStreamFetch(fetchNextOrders, fetchNextOrdersSaga),
    takeLatest(refreshOrdersStreams, refreshOrdersStreamsSaga),
    takeLatest(acceptOriginalOrderSuccess, originalsOrderFlowSuccessRefreshSaga),
    takeLatest(declineOriginalOrderSuccess, originalsOrderFlowSuccessRefreshSaga),
  ]);
}
